import React, { useEffect, useState, useCallback } from 'react';
import { mediaAPI } from '../api/client';
import { Button, Badge, ConfirmDialog, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';

const MEDIA_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1', '');

function MediaItem({ item, onDelete, onSelect, selectable }) {
  const [err, setErr] = useState(false);
  const url = `${MEDIA_BASE}${item.media_url}`;
  const hrs = (Date.now() - new Date(item.created_at.replace(' ', 'T'))) / 3600000;
  const left = 48 - hrs;
  const expColor = left < 6 ? '#ef4444' : '#f59e0b';
  const expLabel = left <= 0 ? '⚠️ Por eliminar' : `⏱ ${left < 1 ? '< 1h' : Math.round(left) + 'h'} restantes`;
  return (
    <div onClick={() => selectable && onSelect && onSelect(item)} className="card-touch"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', cursor: selectable ? 'pointer' : 'default', transition: 'var(--transition)' }}>
      <div style={{ position: 'relative', paddingBottom: '75%', background: 'var(--bg-elevated)' }}>
        {item.media_type === 'video' ? (
          <>
            <video src={url} muted preload="metadata" onError={() => setErr(true)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
              <span style={{ fontSize: '28px', color: '#fff' }}>▶</span>
            </div>
          </>
        ) : err ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px' }}>🖼</div>
        ) : (
          <img src={url} alt="media" onError={() => setErr(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
          <Badge color={item.media_type === 'video' ? '#7c3aed' : '#06b6d4'}>
            {item.media_type === 'video' ? '🎬' : '🖼'} {item.media_type}
          </Badge>
        </div>
        {!selectable && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(item); }}
            style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(239,68,68,0.85)', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', fontSize: '12px' }}>🗑</button>
        )}
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '3px' }}>
          {item.filename}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '5px' }}>
          📅 {new Date(item.created_at.replace(' ', 'T')).toLocaleDateString('es-CO', {day: '2-digit', month: 'short', year: 'numeric'})} · {item.size_mb.toFixed(1)} MB
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '10px', color: expColor, fontWeight: '600' }}>{expLabel}</span>
          {selectable && <span style={{ fontSize: '11px', color: 'var(--accent-primary)', fontWeight: '600' }}>Usar →</span>}
        </div>
      </div>
    </div>
  );
}

export default function MediaLibraryPage({ selectable = false, onSelect }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [filter, setFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mediaAPI.list();
      setItems(data);
    } catch { toast.error('Error cargando biblioteca'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (item) => {
    try {
      await mediaAPI.delete(item.id);
      setItems(i => i.filter(x => x.id !== item.id));
      toast.success('Archivo eliminado');
    } catch { toast.error('Error al eliminar'); }
    setDeleting(null);
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.media_type === filter);
  const totalSize = items.reduce((a, i) => a + i.size_mb, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {!selectable && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
            {items.length} archivos · {totalSize.toFixed(1)} MB usados<br/><span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>⏱ Los archivos se conservan 48h y luego se eliminan automáticamente</span>
          </p>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['all', 'image', 'video'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontFamily: 'var(--font-body)', cursor: 'pointer',
                background: filter === f ? 'var(--gradient-primary)' : 'var(--bg-elevated)',
                border: `1px solid ${filter === f ? 'transparent' : 'var(--border)'}`,
                color: filter === f ? '#fff' : 'var(--text-muted)',
              }}>
                {f === 'all' ? 'Todo' : f === 'image' ? '🖼 Imágenes' : '🎬 Videos'}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ borderRadius: 'var(--radius-lg)', paddingBottom: '100%' }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="🖼" title="Biblioteca vacía" description="Los archivos que subas al publicar aparecerán aquí para reutilizarlos." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
          {filtered.map(item => (
            <MediaItem key={item.id} item={item} onDelete={() => setDeleting(item)} onSelect={onSelect} selectable={selectable} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => handleDelete(deleting)}
        title="Eliminar archivo"
        message={`¿Eliminar "${deleting?.filename}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar" danger
      />
    </div>
  );
}
